import React, { useEffect, useRef } from 'react';

/*
 * ── Bloom Ring Dispersion (Canvas) ────────────────────────────────────────
 *
 * Renders all rings onto a single <canvas> element via Path2D + rAF.
 * No DOM mutations per frame → no SVG layout recalculation → no jitter.
 * Path2D objects are built once; ctx.stroke(path) is GPU-composited.
 *
 * Phase 1 (Hero):  rings disperse outward as user scrolls
 * Phase 2 (About): dispersed rings persist, thicker strokes
 * Phase 3 (Blog):  dimmer, but still present
 */

function seededRand(a, b) {
  const v = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return v - Math.floor(v);
}

function easeOutQuart(t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - Math.pow(1 - t, 4);
}

function smoothstep(t) {
  t = Math.max(0, Math.min(1, t));
  return t * t * (3 - 2 * t);
}

function easeOutCubic(t) {
  t = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - t, 3);
}

// Frame-rate independent lerp
function lerpDt(cur, tar, factor, dt) {
  return cur + (tar - cur) * (1 - Math.pow(1 - factor, dt / 16.667));
}

const DEG = Math.PI / 180;

// Ring configs — innermost first.
// rotDegPerSec matches original CSS: cw/ccw at the same period.
// segs are SVG arc path strings in a 340×340 coordinate space centred at (0,0).
const RINGS = [
  {
    delay: 0.00, maxDist: 30, maxScale: 1.08, spin: 12,  baseOp: 0.18, boostedOp: 0.55,
    rotDegPerSec: -(360 / 70),   // ccw 70 s
    segs: [
      'M -6,-35.5 A 36,36 0 0,1 34,11',
      'M 25,25 A 36,36 0 0,1 -32,-16',
    ],
  },
  {
    delay: 0.06, maxDist: 38, maxScale: 1.10, spin: -10, baseOp: 0.15, boostedOp: 0.50,
    rotDegPerSec:  (360 / 90),   // cw 90 s
    segs: [
      'M 0,-56 A 56,56 0 0,1 54,14',
      'M 34,44 A 56,56 0 0,1 -34,44',
      'M -54,14 A 56,56 0 0,1 0,-56',
    ],
  },
  {
    delay: 0.12, maxDist: 46, maxScale: 1.12, spin: 8,   baseOp: 0.12, boostedOp: 0.45,
    rotDegPerSec: -(360 / 110),  // ccw 110 s
    segs: [
      'M 48,-60 A 76,76 0 0,1 72,24',
      'M 48,58 A 76,76 0 0,1 -48,58',
      'M -72,24 A 76,76 0 0,1 -48,-60',
    ],
  },
  {
    delay: 0.18, maxDist: 54, maxScale: 1.14, spin: -7,  baseOp: 0.10, boostedOp: 0.40,
    rotDegPerSec:  (360 / 140),  // cw 140 s
    segs: [
      'M 0,-96 A 96,96 0 0,1 96,0',
      'M 60,75 A 96,96 0 0,1 -96,0',
    ],
  },
  {
    delay: 0.25, maxDist: 62, maxScale: 1.16, spin: 5,   baseOp: 0.08, boostedOp: 0.35,
    rotDegPerSec: -(360 / 170),  // ccw 170 s
    segs: [
      'M 30,-116 A 118,118 0 0,1 118,0',
      'M 82,85 A 118,118 0 0,1 -82,85',
      'M -118,0 A 118,118 0 0,1 -30,-116',
    ],
  },
  {
    delay: 0.33, maxDist: 70, maxScale: 1.18, spin: -4,  baseOp: 0.06, boostedOp: 0.30,
    rotDegPerSec:  (360 / 200),  // cw 200 s
    segs: [
      'M 0,-140 A 140,140 0 0,1 121.2,-70',
      'M 132.3,46.6 A 140,140 0 0,1 0,140',
      'M -132.3,46.6 A 140,140 0 0,1 -121.2,-70',
    ],
  },
];

// Pre-compute deterministic per-segment jitter — same values as original
const JITTER = RINGS.map((ring, r) =>
  ring.segs.map((_, p) => {
    const base = (p / ring.segs.length) * 360 + r * 53;
    const jit  = seededRand(r, p) * 40 - 20;
    return {
      angle:      (base + jit) * DEG,
      distMult:   0.85 + seededRand(r + 7, p + 3) * 0.3,
      scaleMult:  0.9  + seededRand(r + 2, p + 5) * 0.2,
      spinOffset: (seededRand(r + 4, p + 1) - 0.5) * 18,
      speedMult:  0.7  + seededRand(r + 9, p + 6) * 0.6,
    };
  })
);

export default function Rings() {
  const cvRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');

    // Build Path2D once — reused every frame at zero cost
    const paths = RINGS.map(ring => ring.segs.map(d => new Path2D(d)));

    let W = 0, H = 0, dpr = 1;

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W   = window.innerWidth;
      H   = window.innerHeight;
      cv.width        = W * dpr;
      cv.height       = H * dpr;
      cv.style.width  = W + 'px';
      cv.style.height = H + 'px';
    }
    resize();

    const t0 = performance.now(); // reference for continuous ring rotation

    let curBloom = 0, tarBloom = 0;
    let curPage  = 0, tarPage  = 0;
    let isRunning = false;
    let lastTs    = 0;

    function draw(ts) {
      const dt = lastTs > 0 ? Math.min(ts - lastTs, 50) : 16.667;
      lastTs = ts;

      curBloom = lerpDt(curBloom, tarBloom, 0.055, dt);
      curPage  = lerpDt(curPage,  tarPage,  0.045, dt);

      const doneBloom = Math.abs(curBloom - tarBloom) < 0.0005;
      const donePage  = Math.abs(curPage  - tarPage)  < 0.0005;
      if (doneBloom) curBloom = tarBloom;
      if (donePage)  curPage  = tarPage;

      const bp = curBloom;
      const pp = curPage;

      // Phase progression (same thresholds as before)
      const phase2 = Math.max(0, Math.min((pp - 0.33) / 0.33, 1));
      const phase3 = Math.max(0, Math.min((pp - 0.66) / 0.34, 1));

      // Wrapper scale
      const wrapScale = 1
        + smoothstep(bp)     * 0.25
        + smoothstep(phase2) * 0.12
        + smoothstep(phase3) * 0.08;

      // Wrapper opacity — starts fading at 40 % scroll
      const fadeProg = Math.max(0, (pp - 0.4) / 0.6);
      const wrapOp   = 1 - easeOutCubic(fadeProg);

      // Stroke width (thickens in later phases)
      const sw = 0.8 + smoothstep(phase2) * 1.2 + smoothstep(phase3) * 1.0;

      // Elapsed time drives the continuous ring rotation (never pauses)
      const elapsedSec = (ts - t0) / 1000;

      // --- Canvas draw ---
      // Reset to DPR-only transform, clear, then build coordinate system
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      if (wrapOp < 0.001) {
        // Fully faded — stop loop; restart on next scroll
        if (doneBloom && donePage) { isRunning = false; return; }
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      // Map SVG 340×340 coordinate space → canvas pixels
      // max(130vw, 130vh) matches original CSS sizing
      const displaySize = Math.max(W * 1.3, H * 1.3);
      const svgScale    = (displaySize / 340) * wrapScale;

      ctx.save();
      ctx.translate(W / 2, H / 2); // origin = ring centre
      ctx.scale(svgScale, svgScale);

      ctx.strokeStyle = '#1a1a2e';
      ctx.lineCap     = 'round';
      ctx.lineWidth   = sw;

      for (let r = 0; r < RINGS.length; r++) {
        const ring   = RINGS[r];
        const jits   = JITTER[r];
        const rPaths = paths[r];

        // Continuous rotation — computed from wall-clock time, not loop state,
        // so it never "freezes" even if the loop was paused while faded
        const ringRad = ring.rotDegPerSec * elapsedSec * DEG;

        const raw   = (bp - ring.delay) / (1 - ring.delay);
        const ringP = Math.max(0, Math.min(1, raw));

        for (let s = 0; s < rPaths.length; s++) {
          const j = jits[s];

          const segP       = Math.max(0, Math.min(1, ringP / j.speedMult));
          const e          = easeOutQuart(Math.min(segP, 1));
          const totalDrift = e + e * (easeOutCubic(phase2) * 0.3 + easeOutCubic(phase3) * 0.2);

          const dist = totalDrift * ring.maxDist * j.distMult;
          const tx   = Math.cos(j.angle) * dist;
          const ty   = Math.sin(j.angle) * dist;
          const rot  = totalDrift * (ring.spin + j.spinOffset) * DEG;
          const sc   = 1 + totalDrift * (ring.maxScale - 1) * j.scaleMult * 0.6;

          // Opacity
          const bloomOp = ring.baseOp + (ring.boostedOp - ring.baseOp) * smoothstep(ringP);
          const p2Op    = ring.boostedOp * (1 - smoothstep(phase2) * 0.25);
          const p3Op    = ring.boostedOp * 0.75 * (1 - smoothstep(phase3) * 0.4);
          const segOp   = pp <= 0.33 ? bloomOp : pp <= 0.66 ? p2Op : p3Op;

          ctx.globalAlpha = Math.max(0, segOp * wrapOp);

          // Transform stack replicates original SVG exactly:
          //   parent <g> CSS rotation  →  ctx.rotate(ringRad)
          //   path transform="translate(tx,ty) rotate(rot) scale(sc)"
          //     → ctx.translate; ctx.rotate; ctx.scale
          // Matrix product is identical; no transform-origin ambiguity on canvas.
          ctx.save();
          ctx.rotate(ringRad);
          ctx.translate(tx, ty);
          ctx.rotate(rot);
          ctx.scale(sc, sc);
          ctx.stroke(rPaths[s]);
          ctx.restore();
        }
      }

      ctx.restore();

      // Keep running while visible (rotation needs continuous frames)
      rafRef.current = requestAnimationFrame(draw);
    }

    function startLoop() {
      if (!isRunning) {
        isRunning = true;
        lastTs    = 0;
        rafRef.current = requestAnimationFrame(draw);
      }
    }

    function onScroll() {
      const sy        = window.scrollY || window.pageYOffset;
      const vh        = window.innerHeight;
      const maxScroll = document.documentElement.scrollHeight - vh;
      tarBloom = Math.min(sy / (vh * 2.0), 1);
      tarPage  = maxScroll > 0 ? Math.min(sy / maxScroll, 1) : 0;
      startLoop();
    }

    // Pause when tab is hidden (saves battery on mobile)
    function onVisibility() {
      if (document.hidden) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        isRunning = false;
        lastTs    = 0;
      } else {
        startLoop();
      }
    }

    window.addEventListener('scroll', onScroll,   { passive: true });
    window.addEventListener('resize', resize,      { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    onScroll();
    startLoop();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={cvRef}
      aria-hidden="true"
      style={{
        position:      'fixed',
        top:           0,
        left:          0,
        width:         '100vw',
        height:        '100vh',
        zIndex:        1,
        pointerEvents: 'none',
      }}
    />
  );
}
